import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { getFormValues } from "redux-form";

import { articleFetchTrigger } from "@/mk2/apps/wiki/containers/Article/Article.actions";
import articlesReducer from "@/mk2/apps/wiki/containers/Article/Article.reducers";
import articlesSaga from "@/mk2/apps/wiki/containers/Article/Article.sagas";
import { getWikiArticleState } from "@/mk2/apps/wiki/containers/Article/Article.selectors";
import ArticleAddExperience from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperience";
import {
    articleAddExperienceSaveDraft,
    articleAddExperienceSubmit,
    articleAddExperienceSubmitUnauthenticated,
} from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperience.actions";
import { ARTICLE_ADD_EXPERIENCE_FORM_NAME } from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperience.constants";
import articleAddExperienceSaga from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperience.sagas";
import { WIKI_REVIEWS_ARTICLE_SLUG } from "@/mk2/apps/wiki/helpers";
import {
    getWikiArticleUrl,
    wikiAddExperienceToUndefinedArticleUrl,
    wikiAddExperienceUrl,
} from "@/mk2/apps/wiki/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { SubmitButton } from "@/mk2/components/forms/SubmitButton";
import {
    mkReduxFormMapDispatchToProps,
    mkReduxFormMapStateToProps,
} from "@/mk2/decorators/mkReduxForm";
import { Photo } from "@/mk2/decorators/withUploader";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import FormPage, { FormPageDispatchProps, FormPageStateProps } from "@/mk2/pages/FormPage";
import { PageRouteProps } from "@/mk2/pages/Page";
import {
    AppState,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { UserEntity, WikiArticleEntity, WikiArticleSchema } from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import {
    ARTICLE_ADD_EXPERIENCE_TITLE,
    ARTICLE_EXPERIENCE_SEND,
    SEO_TITLE_WIKI_ARTICLE_ADD_EXPERIENCE,
    WIKI_EXP_DRAFT_SAVE_AS_DRAFT,
} from "@/mk/autogenerated/translation";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";

export interface NewExperienceFormData {
    articleProposal?: string;
    title: string;
    text: string;
    positives?: string;
    negatives?: string;
    rating?: number;
    photos: Photo[];
    createAutomaticPost: boolean;
    wantReviewVerified: boolean;
    wasUsedByUser: string;
    prefilledUsername?: string;
    prefilledPhone?: string;
    prefilledEmail?: string;
    verifiedByPhoneCall?: boolean;
}

export interface OwnProps {}

export interface StateProps extends FormPageStateProps {
    articleSlug: string;
    categorySlug: string;
    isMobile: boolean;
    requestUser: UserEntity;
    article: WikiArticleEntity;
    loadingState: LoadingState;
    afterSaveGotoUrl: string;
    successFlag: boolean;
    next: string; // review article slug
    initialValues: NewExperienceFormData;
    currentValues: NewExperienceFormData;
    isChecked: boolean;
    isUnauthenticatedUser: boolean;
    isAdmin: boolean;
}

export interface DispatchProps extends FormPageDispatchProps {
    onLoad(articleSlug: string, categorySlug: string);
    onSave(
        articleSlug: string,
        categorySlug: string,
        values: NewExperienceFormData,
        gotoUrl: string,
    );
    onSaveUnauthenticated(
        articleSlug: string | null,
        categorySlug: string | null,
        values: NewExperienceFormData,
    );
    onSaveDraft(
        values: NewExperienceFormData,
        articleSlug: string | null,
        categorySlug: string | null,
    );
}

type RoutePropsAddExpToDefinedArticle = PageRouteProps<{
    articleSlug: string;
    categorySlug: string;
}>;
type RoutePropsAddExpToUndefinedArticle = PageRouteProps<{}>;
export type RouteProps = RoutePropsAddExpToDefinedArticle | RoutePropsAddExpToUndefinedArticle;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ArticleAddExperiencePage extends FormPage<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const qp = parse(ownProps.location.search) || {};
            const successFlag = !!qp.success;
            const next = qp.next?.toString();

            let articleSlug;
            let categorySlug;
            if (ownProps.routePathPattern === wikiAddExperienceUrl) {
                const ownPropsAddExpToDefinedArticle = ownProps as OwnProps &
                    RoutePropsAddExpToDefinedArticle;
                articleSlug = ownPropsAddExpToDefinedArticle.routeParams.articleSlug;
                categorySlug = ownPropsAddExpToDefinedArticle.routeParams.categorySlug;
            }
            if (ownProps.routePathPattern === wikiAddExperienceToUndefinedArticleUrl) {
                articleSlug = null;
                categorySlug = null;
            }
            const requestUser = getRequestUser(state);
            const requestUserPermissions = getRequestPermissions(state);
            const isAdmin = requestUserPermissions.indexOf("wiki.can_edit_wiki") >= 0;
            const articleState = getWikiArticleState(state, categorySlug, articleSlug);
            const article: WikiArticleEntity = getDenormalizedEntity<WikiArticleEntity>(
                state,
                WikiArticleSchema,
                articleState.articleId,
            );
            const afterSaveGotoUrl =
                !categorySlug && !articleSlug
                    ? url(wikiAddExperienceToUndefinedArticleUrl, {}, { success: true })
                    : getWikiArticleUrl(categorySlug, articleSlug);

            return {
                ...mkReduxFormMapStateToProps(ARTICLE_ADD_EXPERIENCE_FORM_NAME)(state),
                articleSlug,
                categorySlug,
                isMobile: getRequestDeviceMobile(state),
                requestUser,
                article,
                loadingState: articleState.loadingState,
                afterSaveGotoUrl,
                successFlag,
                next,
                isChecked: false,
                isUnauthenticatedUser: !isAuthenticated(requestUser),
                isAdmin,
                initialValues: {
                    title: "",
                    text: "",
                    photos: [],
                    wantReviewVerified: false,
                    wasUsedByUser: "",
                    createAutomaticPost: Boolean(categorySlug && articleSlug),
                    prefilledUsername: "",
                    prefilledPhone: "",
                    prefilledEmail: "",
                    verifiedByPhoneCall: false,
                },
                currentValues: (getFormValues(ARTICLE_ADD_EXPERIENCE_FORM_NAME)(state) ||
                    {}) as NewExperienceFormData,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...mkReduxFormMapDispatchToProps(ARTICLE_ADD_EXPERIENCE_FORM_NAME),
            onLoad: articleFetchTrigger,
            onSave: articleAddExperienceSubmit,
            onSaveUnauthenticated: articleAddExperienceSubmitUnauthenticated,
            onSaveDraft: articleAddExperienceSaveDraft,
        };
    }

    public getPermissionRequired(_props: Props): boolean {
        return true;
    }

    public getMetaTitle(props: Props): string {
        return !props.successFlag ? SEO_TITLE_WIKI_ARTICLE_ADD_EXPERIENCE : null; // showing success/addAnother div
    }

    public render(props: Props): false | JSX.Element {
        return <ArticleAddExperience {...props} />;
    }

    public getHeaderRight(props): React.ReactElement {
        return !props.successFlag ? super.getHeaderRight(props) : null;
    }

    public getHeaderAdditionalButtons(props: Props): React.ReactElement {
        if (!props.isAdmin || props.successFlag) return null;
        const { currentValues, articleSlug, categorySlug, onSaveDraft } = props;
        return (
            <Btn
                type={BtnType.Admin}
                label={WIKI_EXP_DRAFT_SAVE_AS_DRAFT}
                onClick={() => onSaveDraft(currentValues, articleSlug, categorySlug)}
            />
        );
    }

    public getSubmitButton(props: Props): React.ReactElement {
        return !props.successFlag ? (
            <SubmitButton
                btnType={BtnType.Blue}
                label={ARTICLE_EXPERIENCE_SEND}
                formName={ARTICLE_ADD_EXPERIENCE_FORM_NAME}
            />
        ) : null;
    }

    public getFooter(props: Props): React.ReactNode {
        return !props.successFlag ? this.getSubmitButton(props) : null;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return !props.successFlag;
    }

    public getDefaultBackPath(props: Props): string {
        const { articleSlug, categorySlug } = props;
        if (articleSlug && categorySlug) {
            return getWikiArticleUrl(categorySlug, articleSlug);
        } else {
            return getWikiArticleUrl(WIKI_REVIEWS_ARTICLE_SLUG, WIKI_REVIEWS_ARTICLE_SLUG);
        }
    }

    public getHeaderTitle(props: Props): string {
        return !props.successFlag ? ARTICLE_ADD_EXPERIENCE_TITLE : null;
    }

    public getHeaderTabs(props: Props): JSX.Element[] | null {
        return null;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.wiki.article", articlesReducer);
        store.injectSaga("containers.wiki.article", articlesSaga);

        store.injectSaga("containers.wiki.articleAddExperience", articleAddExperienceSaga);
    }
}
